import React, { useState } from 'react';

/**
 * The weekly work pattern — one row per day, so Tuesday can differ from Monday.
 *
 * Shared by the Add & Edit Staff form and the Recruitment transfer, because it
 * is the same grid writing the same columns either way. Kept in step with
 * api/utils/weekPattern.js, which enforces the same rules on save and derives
 * the legacy working_days / shift_start / shift_end from this.
 *
 * A care home reads these rows as SHIFTS — Working / Start / Finish / Unpaid
 * break, with paid hours following from them. A domiciliary service reads the
 * same rows as AVAILABILITY — Available / from / until, no break and no paid
 * hours, because the visits themselves are assigned later in Rota. Which
 * reading applies comes from the organisation's own type, never a switch on the
 * screen, so one service can't accidentally record the other's meaning.
 *
 * @param {Array}    value        seven entries: { day, on, start, end, break_minutes }
 * @param {Function} onChange     called with the next array
 * @param {boolean}  domiciliary  availability rather than shifts
 */

export const DAYS_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

/**
 * Monday to Friday, eight-to-four with a half-hour break: the pattern most
 * staff are on, so the manager confirms it rather than building it.
 */
export const defaultWeekPattern = () => DAYS_OF_WEEK.map((day) => ({
  day,
  on: !['Saturday', 'Sunday'].includes(day),
  start: '08:00',
  end: '16:00',
  break_minutes: 30,
}));

export const minutesOfTime = (hm) => {
  const match = String(hm || '').match(/^(\d{1,2}):(\d{2})/);
  if (!match) return null;
  return (Number(match[1]) * 60) + Number(match[2]);
};

/**
 * Paid hours for one day, minus the unpaid break.
 *
 * A finish at or before the start is an OVERNIGHT shift and rolls into the next
 * day — 20:00 to 08:00 is twelve hours, not minus twelve.
 */
export const paidHoursFor = (entry) => {
  if (!entry?.on) return 0;
  const start = minutesOfTime(entry.start);
  const end = minutesOfTime(entry.end);
  if (start === null || end === null) return 0;
  const span = end > start ? end - start : (end + (24 * 60)) - start;
  const paid = span - (Number(entry.break_minutes) || 0);
  return paid > 0 ? Math.round((paid / 60) * 100) / 100 : 0;
};

/** Seven days in order, whatever the API sent — a missing day comes back off. */
export const normalizeWeekPattern = (raw, { domiciliary = false } = {}) => {
  const list = Array.isArray(raw) ? raw : [];
  const byDay = new Map(
    list.map((entry, index) => [
      DAYS_OF_WEEK.find((d) => d.toLowerCase() === String(entry?.day || '').toLowerCase())
        || DAYS_OF_WEEK[index],
      entry,
    ]),
  );

  return DAYS_OF_WEEK.map((day) => {
    const entry = byDay.get(day) || {};
    return {
      day,
      on: entry.on === true || entry.working === true || entry.available === true,
      start: String(entry.start ?? entry.from ?? '08:00').slice(0, 5),
      end: String(entry.end ?? entry.until ?? '16:00').slice(0, 5),
      break_minutes: domiciliary ? 0 : (Number(entry.break_minutes ?? 30) || 0),
    };
  });
};

/** Total paid hours across the week — what the caller shows in its summary. */
export const weeklyPaidHours = (pattern) => Math.round(
  (pattern || []).reduce((sum, entry) => sum + paidHoursFor(entry), 0) * 100,
) / 100;

const FIELD = 'w-full px-3 py-2 bg-white dark:bg-[#31373f] dark:text-gray-200 border border-[#CBD5E1] dark:border-gray-700 rounded-lg text-[13.5px] focus:outline-none focus:ring-2 focus:ring-blue-500/10 focus:border-blue-500 transition';

const WeekPatternGrid = ({ value, onChange, domiciliary = false }) => {
  // The bulk row under the grid: set one shift, then push it to every day that
  // is on. Saves typing the same times seven times, which is the common case.
  const [applyRow, setApplyRow] = useState({ start: '08:00', end: '16:00', break_minutes: 30 });

  const pattern = Array.isArray(value) && value.length ? value : defaultWeekPattern();

  // What the grid adds up to. Computed from the same paidHoursFor() each row
  // shows, so the total and the rows above it can never disagree — and from the
  // same function the API recomputes on save (api/utils/weekPattern.js).
  const workingDays = pattern.filter((entry) => entry.on).length;
  const totalPaid = weeklyPaidHours(pattern);

  const setDay = (day, changes) => onChange(
    pattern.map((entry) => (entry.day === day ? { ...entry, ...changes } : entry)),
  );

  const applyToWorkingDays = () => onChange(pattern.map((entry) => (entry.on
    ? {
      ...entry,
      start: applyRow.start,
      end: applyRow.end,
      break_minutes: domiciliary ? 0 : (Number(applyRow.break_minutes) || 0),
    }
    : entry)));

  return (
    <div className="border border-[#E2E8F0] dark:border-gray-800 rounded-2xl overflow-hidden">
      {/* Scrolls sideways rather than squashing the time inputs on a narrow
          screen — a half-visible time field is worse than a scrollbar. */}
      <div className="overflow-x-auto">
        <table className="w-full min-w-[720px] text-left">
          <thead className="bg-[#F8FAFC] dark:bg-[#20242c]">
            <tr className="text-[11px] font-bold text-[#64748B] dark:text-gray-400 uppercase tracking-wider">
              <th className="px-5 py-3">Day</th>
              <th className="px-5 py-3">{domiciliary ? 'Available' : 'Working'}</th>
              <th className="px-5 py-3">{domiciliary ? 'Available from' : 'Start'}</th>
              <th className="px-5 py-3">{domiciliary ? 'Available until' : 'Finish'}</th>
              {!domiciliary && <th className="px-5 py-3">Unpaid break</th>}
              {!domiciliary && <th className="px-5 py-3 text-right">Paid hours</th>}
            </tr>
          </thead>
          <tbody>
            {pattern.map((entry) => {
              const paid = paidHoursFor(entry);
              const startMinutes = minutesOfTime(entry.start);
              const endMinutes = minutesOfTime(entry.end);
              const isOvernight = entry.on
                && startMinutes !== null && endMinutes !== null
                && endMinutes <= startMinutes;

              return (
                <tr
                  key={entry.day}
                  className={`border-t border-[#E2E8F0] dark:border-gray-800 ${entry.on ? '' : 'bg-[#F8FAFC] dark:bg-[#20242c]/60'}`}
                >
                  <td className="px-5 py-3 text-[13.5px] font-bold text-[#1E293B] dark:text-gray-200 whitespace-nowrap">
                    {entry.day}
                  </td>
                  <td className="px-5 py-3">
                    <label className="inline-flex items-center gap-2 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={entry.on}
                        onChange={() => setDay(entry.day, { on: !entry.on })}
                        className="w-4 h-4 accent-blue-600"
                      />
                      <span className={`text-[13px] font-medium ${entry.on ? 'text-[#1E293B] dark:text-gray-200' : 'text-[#94A3B8] dark:text-gray-500'}`}>
                        {entry.on
                          ? (domiciliary ? 'Available' : 'Working')
                          : (domiciliary ? 'Not available' : 'Day off')}
                      </span>
                    </label>
                  </td>
                  <td className="px-5 py-3">
                    <input
                      type="time"
                      value={entry.start}
                      disabled={!entry.on}
                      onChange={(e) => setDay(entry.day, { start: e.target.value })}
                      className={`${FIELD} disabled:bg-[#F1F5F9] dark:disabled:bg-[#20242c] disabled:text-[#94A3B8] dark:disabled:text-gray-600`}
                    />
                  </td>
                  <td className="px-5 py-3">
                    <input
                      type="time"
                      value={entry.end}
                      disabled={!entry.on}
                      onChange={(e) => setDay(entry.day, { end: e.target.value })}
                      className={`${FIELD} disabled:bg-[#F1F5F9] dark:disabled:bg-[#20242c] disabled:text-[#94A3B8] dark:disabled:text-gray-600`}
                    />
                  </td>
                  {!domiciliary && (
                    <td className="px-5 py-3">
                      <input
                        type="number"
                        min="0"
                        step="5"
                        value={entry.break_minutes}
                        disabled={!entry.on}
                        onChange={(e) => setDay(entry.day, { break_minutes: Number(e.target.value) || 0 })}
                        className={`${FIELD} disabled:bg-[#F1F5F9] dark:disabled:bg-[#20242c] disabled:text-[#94A3B8] dark:disabled:text-gray-600`}
                      />
                    </td>
                  )}
                  {!domiciliary && (
                    <td className="px-5 py-3 text-right whitespace-nowrap">
                      <span className={`text-[13.5px] font-bold ${paid > 0 ? 'text-[#1E293B] dark:text-gray-200' : 'text-[#94A3B8] dark:text-gray-500'}`}>
                        {paid.toFixed(1)} h
                      </span>
                      {/* A finish at or before the start is an overnight shift,
                          not a mistake — say so, because the hours look
                          surprising until you know that. */}
                      {isOvernight && (
                        <span className="block text-[11px] font-semibold text-[#2563EB]">overnight</span>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>

          {/* The week's total, under the column it totals.
              The dialog header carries the same number, but that is a summary
              you read before you start; this is the one you check after typing
              seven rows, and it belongs at the foot of the column those rows
              add up. A domiciliary week has no paid hours to total — the grid
              is availability, and nobody is paid for being available. */}
          {!domiciliary && (
            <tfoot>
              <tr className="border-t-2 border-[#E2E8F0] dark:border-gray-800 bg-[#F8FAFC] dark:bg-[#20242c]">
                {/* The column header above already says "Paid hours", so the
                    total needs no label of its own — only what it is the total
                    OF, which is the days that are on. */}
                <td className="px-5 py-3.5 text-[13px] font-bold text-[#1E293B] dark:text-gray-200 whitespace-nowrap" colSpan={5}>
                  Total
                  <span className="ml-2 font-medium text-[#64748B] dark:text-gray-400">
                    {workingDays} working day{workingDays === 1 ? '' : 's'}
                  </span>
                </td>
                <td className="px-5 py-3.5 text-right whitespace-nowrap">
                  <span className="text-[15px] font-bold text-[#1E293B] dark:text-gray-100 tabular-nums">
                    {totalPaid.toFixed(1)} h
                  </span>
                  <span className="block text-[11px] font-semibold text-[#94A3B8] dark:text-gray-500">per week</span>
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-5 py-3.5 bg-[#F8FAFC] dark:bg-[#20242c] border-t border-[#E2E8F0] dark:border-gray-800">
        <p className="text-[12.5px] text-[#64748B] dark:text-gray-400">
          {domiciliary
            ? 'Set the usual hours once, then apply them to every available day.'
            : 'Set a normal shift once, then apply it to every working day.'}
        </p>
        <div className="flex items-center gap-2">
          <input
            type="time"
            value={applyRow.start}
            onChange={(e) => setApplyRow((prev) => ({ ...prev, start: e.target.value }))}
            aria-label="Start time to apply"
            className="px-3 py-2 bg-white dark:bg-[#31373f] dark:text-gray-200 border border-[#CBD5E1] dark:border-gray-700 rounded-lg text-[13px]"
          />
          <input
            type="time"
            value={applyRow.end}
            onChange={(e) => setApplyRow((prev) => ({ ...prev, end: e.target.value }))}
            aria-label="Finish time to apply"
            className="px-3 py-2 bg-white dark:bg-[#31373f] dark:text-gray-200 border border-[#CBD5E1] dark:border-gray-700 rounded-lg text-[13px]"
          />
          {!domiciliary && (
            <input
              type="number"
              min="0"
              step="5"
              value={applyRow.break_minutes}
              onChange={(e) => setApplyRow((prev) => ({ ...prev, break_minutes: e.target.value }))}
              aria-label="Unpaid break minutes to apply"
              className="w-20 px-3 py-2 bg-white dark:bg-[#31373f] dark:text-gray-200 border border-[#CBD5E1] dark:border-gray-700 rounded-lg text-[13px]"
            />
          )}
          <button
            type="button"
            onClick={applyToWorkingDays}
            className="px-3.5 py-2 text-[13px] font-bold text-[#2563EB] hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition whitespace-nowrap"
          >
            {domiciliary ? 'Apply to available days' : 'Apply to working days'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default WeekPatternGrid;
